import React, { useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Phone, CreditCard, Gift, Check, Clock, Shield, Truck, Banknote, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAddress } from '../../contexts/AddressContext';
import { useLocation } from '../../contexts/LocationContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import LocationButton from '../../components/LocationButton';
import GoogleAddressSelector from '../../components/GoogleAddressSelector';
import ZomatoAddressSelector from '../../components/ZomatoAddressSelector';
import LocationPicker from '../../components/LocationPicker';
import { generateOrderId } from '../../lib/utils';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const CheckoutPageNew = () => {
  const navigate = useNavigate();
  const { cartItems, appliedCoupon, getCartTotal, getCartDiscount, getFinalTotal, clearCartSilent, applyCoupon } = useCart();
  const { user, sendOTP, verifyOTP } = useAuth();
  const { savedAddresses, selectedAddress, selectAddress, saveAddress } = useAddress();
  const { currentLocation, getCurrentLocation, isWithinDeliveryArea } = useLocation();
  
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(!!user?.phoneNumber);
  const [isVerifying, setIsVerifying] = useState(false);
  
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: '',
    address: '',
    landmark: '',
    city: 'Jaipur',
    pincode: '302001'
  });
  
  const [couponCode, setCouponCode] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [saveAddressForFuture, setSaveAddressForFuture] = useState(false);
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [showZomatoAddressSelector, setShowZomatoAddressSelector] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/');
      return;
    }
    
    fetchAvailableCoupons();
    
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        if (parsedUser.phone && parsedUser.isLoggedIn) {
          setPhoneNumber(parsedUser.phone);
          setIsOtpSent(true);
          localStorage.setItem('customerPhone', parsedUser.phone);
          setStep(2);
        }
      } catch (error) {
        console.error('Error parsing stored user data on mount:', error);
      }
    }
  }, [cartItems, navigate]);

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    let userPhone = user?.phoneNumber;
    
    if (storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        userPhone = parsedUser.phone || userPhone;
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }
    
    if (userPhone) {
      setPhoneNumber(userPhone);
      setIsOtpSent(true);
      localStorage.setItem('customerPhone', userPhone);
      if (step === 1) {
        setStep(2);
      }
    }
  }, [user, step]);

  const fetchAvailableCoupons = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'coupons'));
      const coupons = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableCoupons(coupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to load coupons.');
    }
  };

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      toast.error('Please enter your mobile number');
      return;
    }
    setIsVerifying(true);
    try {
      const confirmation = await sendOTP(`+91${phoneNumber}`);
      setConfirmationResult(confirmation);
      setIsOtpSent(true);
      toast.success('OTP sent to your mobile number!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }
    setIsVerifying(true);
    try {
      await verifyOTP(confirmationResult, otp, phoneNumber);
      toast.success('Phone number verified!');
      setStep(2);
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const coupon = availableCoupons.find(c => c.code.toLowerCase() === couponCode.toLowerCase());
      
      if (!coupon) {
        toast.error('Invalid coupon code');
        return;
      }

      if (!coupon.isActive) {
        toast.error('This coupon is no longer active');
        return;
      }

      const subtotal = getCartTotal();
      if (subtotal < coupon.minOrderAmount) {
        toast.error(`Minimum order amount should be ₹${coupon.minOrderAmount}`);
        return;
      }

      const couponData = {
        code: coupon.code,
        type: coupon.promotionType || coupon.discountType,
        value: coupon.promotionValue || coupon.discountValue || coupon.value,
        minOrder: coupon.minOrderAmount || coupon.minOrder,
        maxDiscount: coupon.maxDiscountAmount || coupon.maxDiscount
      };
      
      const success = applyCoupon(couponData);
      
      if (success) {
        setCouponCode('');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error('Failed to apply coupon. Please try again.');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleLocationUpdate = (locationData) => {
    if (locationData.address) {
      const addr = locationData.address;
      setDeliveryAddress(prev => ({
        ...prev,
        name: prev.name || 'My Location',
        address: addr.fullAddress || `${addr.street}, ${addr.area}, ${addr.city}`,
        landmark: addr.area || '',
        city: addr.city || 'Jaipur',
        pincode: addr.pincode || '302001'
      }));
      
      if (locationData.location && !isWithinDeliveryArea(locationData.location.latitude, locationData.location.longitude)) {
        toast.error('Sorry, we don\'t deliver to this location yet. Please choose a different address.');
      } else {
        toast.success('Address auto-filled from your location!');
      }
    }
  };

  const handleAddressSelect = (addressData) => {
    setDeliveryAddress({
      name: deliveryAddress.name || '',
      address: addressData.address,
      landmark: deliveryAddress.landmark || '',
      city: addressData.city,
      pincode: addressData.pincode,
      coordinates: addressData.coordinates,
      lat: addressData.lat,
      lng: addressData.lng
    });
    setShowAddressSelector(false);
    toast.success('Address selected successfully!');
  };

  const handleZomatoAddressSelect = (addressData) => {
    setDeliveryAddress({
      name: addressData.name || '',
      address: addressData.address,
      landmark: addressData.landmark || '',
      city: addressData.city,
      pincode: addressData.pincode,
      coordinates: addressData.coordinates,
      lat: addressData.lat,
      lng: addressData.lng
    });
    setShowZomatoAddressSelector(false);
    toast.success('Address selected successfully!');
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setDeliveryAddress(prev => ({
      ...prev,
      address: location.address,
      coordinates: location.coordinates
    }));
    toast.success('Location selected! Address auto-filled.');
  };

  const handlePlaceOrder = async () => {
    if (!deliveryAddress.name || !deliveryAddress.address || !deliveryAddress.pincode) {
      toast.error('Please fill in all required address fields');
      return;
    }

    setIsPlacingOrder(true);
    try {
      const orderData = {
        orderId: generateOrderId(),
        userId: user?.uid || null,
        customerPhone: phoneNumber,
        items: cartItems,
        deliveryAddress: {
          ...deliveryAddress,
          coordinates: currentLocation ? {
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            accuracy: currentLocation.accuracy
          } : null
        },
        appliedCoupon: appliedCoupon?.code || null,
        subtotal: getCartTotal(),
        discount: getCartDiscount(),
        total: getFinalTotal(),
        paymentMethod: paymentMethod,
        status: 'placed',
        paymentStatus: paymentMethod === 'cod' ? 'pending_cod' : 'pending',
        createdAt: new Date(),
        estimatedDelivery: new Date(Date.now() + 30 * 60 * 1000),
        locationData: currentLocation ? {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          accuracy: currentLocation.accuracy,
          timestamp: currentLocation.timestamp
        } : null
      };

      console.log('💾 Saving order data to Firestore:', orderData);
      const docRef = await addDoc(collection(db, 'orders'), orderData);
      console.log('✅ Order saved successfully with ID:', docRef.id);
      
      // Store phone number in localStorage for order history access
      localStorage.setItem('customerPhone', phoneNumber);
      localStorage.setItem('userPhone', phoneNumber);
      
      if (saveAddressForFuture) {
        try {
          const addressSaved = await saveAddress(deliveryAddress);
          if (addressSaved) {
            console.log('✅ Address saved for future use');
          }
        } catch (addressError) {
          console.error('Address save failed, but order was placed:', addressError);
        }
      }
      
      if (paymentMethod === 'cod') {
        toast.success('Order placed successfully! Pay ₹' + (Number(getFinalTotal()) || 0) + ' on delivery.');
      } else {
        toast.success('Order placed successfully!');
      }
      clearCartSilent();
      
      setTimeout(() => {
        navigate('/order');
      }, 1000);
    } catch (error) {
      console.error('Error placing order:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code
      });
      
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please check Firestore rules for orders collection.');
      } else if (error.code === 'unavailable') {
        toast.error('Service temporarily unavailable. Please try again.');
      } else {
        toast.error(`Failed to place order: ${error.message}`);
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const StepIndicator = ({ currentStep }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '24px',
      gap: '12px'
    }}>
      {[1, 2, 3].map((s) => (
        <div key={s} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: s <= currentStep ? '#ff6b35' : '#e5e7eb',
            color: s <= currentStep ? 'white' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '12px'
          }}>
            {s}
          </div>
          {s < 3 && (
            <div style={{
              width: '30px',
              height: '2px',
              backgroundColor: s < currentStep ? '#ff6b35' : '#e5e7eb'
            }}></div>
          )}
        </div>
      ))}
    </div>
  );

  if (cartItems.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        color: '#6b7280',
        padding: '20px',
        textAlign: 'center'
      }}>
        <CreditCard style={{ width: '60px', height: '60px', marginBottom: '20px', color: '#9ca3af' }} />
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>Your cart is empty!</h2>
        <p style={{ fontSize: '16px', marginBottom: '24px' }}>Add some delicious food to proceed to checkout.</p>
        <Button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e05a2e'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
        >
          Go to Homepage
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif',
      paddingBottom: '20px'
    }}>
      {/* Mobile Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#4b5563',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft style={{ width: '20px', height: '20px' }} />
          Back
        </button>

        <h1 style={{
          fontSize: '18px',
          fontWeight: '800',
          color: '#1f2937',
          margin: 0
        }}>
          Checkout
        </h1>

        <div style={{ width: '60px' }} /> {/* Spacer */}
      </div>

      <div style={{ padding: '16px' }}>
        <StepIndicator currentStep={step} />

        {/* Step 1: Phone Verification */}
        {step === 1 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Phone style={{ width: '24px', height: '24px', color: '#ff6b35' }} />
              Verify Your Phone Number
            </h2>
            
            {!isOtpSent ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Mobile Number
                  </Label>
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#6b7280',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      <Phone style={{ width: '18px', height: '18px' }} />
                      <span>+91</span>
                    </div>
                    <Input
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                        setPhoneNumber(value);
                      }}
                      maxLength="10"
                      style={{
                        padding: '14px 14px 14px 60px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        fontSize: '16px',
                        width: '100%',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleSendOTP}
                  disabled={isVerifying || !phoneNumber}
                  style={{
                    padding: '14px 20px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: isVerifying || !phoneNumber ? 'not-allowed' : 'pointer',
                    opacity: isVerifying || !phoneNumber ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                >
                  {isVerifying ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ fontSize: '14px', color: '#4b5563' }}>
                  OTP sent to <span style={{ fontWeight: '600' }}>+91{phoneNumber}</span>. Please enter it below.
                </p>
                
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Enter OTP
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength="6"
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      fontSize: '16px',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      textAlign: 'center',
                      letterSpacing: '2px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Button
                    onClick={handleVerifyOTP}
                    disabled={isVerifying || !otp}
                    style={{
                      padding: '14px 20px',
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isVerifying || !otp ? 'not-allowed' : 'pointer',
                      opacity: isVerifying || !otp ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                      width: '100%'
                    }}
                  >
                    {isVerifying ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  
                  <Button
                    onClick={() => setIsOtpSent(false)}
                    style={{
                      padding: '14px 20px',
                      backgroundColor: 'white',
                      color: '#ff6b35',
                      border: '2px solid #ff6b35',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff7ed'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    Resend OTP / Change Number
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Delivery Address */}
        {step === 2 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb',
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MapPin style={{ width: '24px', height: '24px', color: '#ff6b35' }} />
                Delivery Address
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowZomatoAddressSelector(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
                >
                  <Navigation size={16} />
                  Select Address
                </button>

                <button
                  onClick={() => setShowLocationPicker(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  <MapPin size={16} />
                  Pick Location
                </button>
              </div>
            </h2>

            {/* Saved Addresses */}
            {savedAddresses.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                  Your Saved Addresses
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {savedAddresses.map((address) => (
                    <div
                      key={address.id}
                      onClick={() => {
                        selectAddress(address);
                        setDeliveryAddress({
                          name: address.name,
                          address: address.address,
                          landmark: address.landmark,
                          city: address.city,
                          pincode: address.pincode
                        });
                      }}
                      style={{
                        padding: '12px',
                        borderRadius: '10px',
                        border: selectedAddress?.id === address.id ? '2px solid #ff6b35' : '2px solid #e5e7eb',
                        backgroundColor: selectedAddress?.id === address.id ? '#fff7ed' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0' }}>
                          {address.name}
                        </p>
                        <p style={{ fontSize: '12px', color: '#4b5563', margin: 0 }}>
                          {address.address}, {address.landmark && `${address.landmark}, `}{address.city} - {address.pincode}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              Or Enter New Address
            </h3>

            {/* Location Capture */}
            <div style={{ 
              marginBottom: '16px', 
              padding: '16px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '10px',
              border: '2px dashed #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <MapPin style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                  Pin Your Location
                </h4>
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                Click to automatically detect your location and fill address fields.
              </p>
              <LocationButton 
                onLocationUpdate={handleLocationUpdate}
                style={{ 
                  padding: '10px 20px', 
                  fontSize: '14px',
                  backgroundColor: '#10b981',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Full Name *
                </Label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={deliveryAddress.name}
                  onChange={(e) => setDeliveryAddress({...deliveryAddress, name: e.target.value})}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: '2px solid #e5e7eb',
                    fontSize: '16px',
                    width: '100%',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div>
                <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Complete Address *
                </Label>
                <Input
                  type="text"
                  placeholder="House/Flat No., Building, Street"
                  value={deliveryAddress.address}
                  onChange={(e) => setDeliveryAddress({...deliveryAddress, address: e.target.value})}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: '2px solid #e5e7eb',
                    fontSize: '16px',
                    width: '100%',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Landmark
                  </Label>
                  <Input
                    type="text"
                    placeholder="Near landmark"
                    value={deliveryAddress.landmark}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, landmark: e.target.value})}
                    style={{
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      fontSize: '16px',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Pincode *
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter pincode"
                    value={deliveryAddress.pincode}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, pincode: e.target.value})}
                    style={{
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      fontSize: '16px',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '20px' }}>
                <input
                  type="checkbox"
                  checked={saveAddressForFuture}
                  onChange={(e) => setSaveAddressForFuture(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#ff6b35' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>Save this address for future orders</span>
              </label>

              <Button
                onClick={() => setStep(3)}
                disabled={!deliveryAddress.name || !deliveryAddress.address || !deliveryAddress.pincode}
                style={{
                  padding: '14px 20px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  width: '100%',
                  cursor: deliveryAddress.name && deliveryAddress.address && deliveryAddress.pincode ? 'pointer' : 'not-allowed',
                  opacity: deliveryAddress.name && deliveryAddress.address && deliveryAddress.pincode ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              >
                Proceed to Payment
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Payment Method */}
        {step === 3 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb',
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <CreditCard style={{ width: '24px', height: '24px', color: '#ff6b35' }} />
              Select Payment Method
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <label
                onClick={() => setPaymentMethod('razorpay')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  borderRadius: '10px',
                  border: `2px solid ${paymentMethod === 'razorpay' ? '#ff6b35' : '#e5e7eb'}`,
                  backgroundColor: paymentMethod === 'razorpay' ? '#fff7ed' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="razorpay"
                  checked={paymentMethod === 'razorpay'}
                  onChange={() => setPaymentMethod('razorpay')}
                  style={{ width: '18px', height: '18px' }}
                />
                <Banknote style={{ width: '20px', height: '20px', color: '#ff6b35' }} />
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  Pay Online (Razorpay)
                </span>
              </label>
              
              <label
                onClick={() => setPaymentMethod('cod')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  borderRadius: '10px',
                  border: `2px solid ${paymentMethod === 'cod' ? '#ff6b35' : '#e5e7eb'}`,
                  backgroundColor: paymentMethod === 'cod' ? '#fff7ed' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked={paymentMethod === 'cod'}
                  onChange={() => setPaymentMethod('cod')}
                  style={{ width: '18px', height: '18px' }}
                />
                <Truck style={{ width: '20px', height: '20px', color: '#ff6b35' }} />
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  Cash on Delivery
                </span>
              </label>
            </div>

            <Button
              onClick={handlePlaceOrder}
              disabled={isPlacingOrder}
              style={{
                padding: '16px 20px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '18px',
                fontWeight: '700',
                width: '100%',
                cursor: isPlacingOrder ? 'not-allowed' : 'pointer',
                opacity: isPlacingOrder ? 0.6 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isPlacingOrder ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Placing Order...
                </>
              ) : (
                `Place Order (₹${Number(getFinalTotal()) || 0})`
              )}
            </Button>
          </div>
        )}

        {/* Order Summary - Mobile Optimized */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <CreditCard style={{ width: '20px', height: '20px', color: '#ff6b35' }} />
            Order Summary
          </h2>

          <div style={{ marginBottom: '16px', borderBottom: '1px dashed #e5e7eb', paddingBottom: '16px' }}>
            {cartItems.map(item => {
              // Debug: Log the item to see what's in the cart
              console.log('Cart item:', item);
              
              // Ensure price is a number
              let itemPrice = 0;
              if (typeof item.price === 'object' && item.price !== null) {
                itemPrice = item.price[item.portion] || item.price.half || 0;
              } else if (typeof item.price === 'number') {
                itemPrice = item.price;
              } else {
                itemPrice = 0;
              }
              itemPrice = Number(itemPrice) || 0;
              
              return (
                <div key={`${item.id}-${item.portion}`} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      backgroundColor: '#f3f4f6',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#4b5563'
                    }}>
                      {item.quantity}x
                    </span>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                        {item.name} ({item.portion})
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                        ₹{itemPrice} each
                      </p>
                    </div>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    ₹{itemPrice * item.quantity}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom: '16px', borderBottom: '1px dashed #e5e7eb', paddingBottom: '16px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px'
            }}>
              <span style={{ fontSize: '14px', color: '#4b5563' }}>Subtotal ({cartItems.length} items)</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                ₹{getCartTotal()}
              </span>
            </div>
            
            {appliedCoupon && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px'
              }}>
                <span style={{ fontSize: '14px', color: '#059669' }}>
                  Discount ({appliedCoupon.type === 'percentage' ? `${appliedCoupon.value}%` : `₹${appliedCoupon.value || 0}`})
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                  -₹{getCartDiscount()}
                </span>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '12px'
            }}>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Total</span>
              <span style={{ fontSize: '18px', fontWeight: '800', color: '#ff6b35' }}>
                ₹{getFinalTotal()}
              </span>
            </div>
          </div>

          {/* Apply Coupon Section */}
          <div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Gift style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
              Apply Coupon
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <Button
                onClick={handleApplyCoupon}
                disabled={!couponCode.trim() || isApplyingCoupon}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: couponCode.trim() && !isApplyingCoupon ? 'pointer' : 'not-allowed',
                  opacity: couponCode.trim() && !isApplyingCoupon ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              >
                {isApplyingCoupon ? 'Applying...' : 'Apply'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Google Address Selector Modal */}
      <GoogleAddressSelector
        isOpen={showAddressSelector}
        onAddressSelect={handleAddressSelect}
        onClose={() => setShowAddressSelector(false)}
        initialAddress={deliveryAddress}
      />

      {/* Zomato-style Address Selector Modal */}
      <ZomatoAddressSelector
        isOpen={showZomatoAddressSelector}
        onAddressSelect={handleZomatoAddressSelect}
        onClose={() => setShowZomatoAddressSelector(false)}
        savedAddresses={savedAddresses}
      />

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                Select Your Delivery Location
              </h2>
              <button
                onClick={() => setShowLocationPicker(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                ✕
              </button>
            </div>

            {/* Location Picker */}
            <LocationPicker
              onLocationSelect={handleLocationSelect}
              initialLocation={selectedLocation}
            />

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowLocationPicker(false)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedLocation) {
                    setShowLocationPicker(false);
                    toast.success('Location selected! You can now proceed with checkout.');
                  } else {
                    toast.error('Please select a location on the map first.');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              >
                Use This Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPageNew;